/**
 * Stale-while-revalidate cache for the Buyer Service Invoice list
 * (/operations/service-invoice-list, buyer view).
 *
 * Author: Akarsh Singh | 2026-09-12 | Page-optimization track, Phase 1.
 *
 * Why this exists: the screen had no cache at either tier, so every revisit,
 * status chip, sort and page change paid a full round trip — measured at
 * ~938ms for the list and ~912ms for the status summary against a ~554ms
 * API-Gateway floor (baseline 2026-09-12).
 *
 * This screen has three reads rather than one, so the store is keyed by a
 * caller-supplied string and fed by a caller-supplied fetcher. That keeps one
 * cache serving the active list, the archived list and the status summary
 * without three near-identical services.
 *
 * Two rules this file must keep:
 *  1. Every key carries `buyerId`. This service is `providedIn: 'root'`, so the
 *     store outlives navigation and re-login; a key without the viewer's id
 *     would serve one buyer's invoices to another.
 *  2. `invalidateAll()` bumps an epoch, so a read that was already in the air
 *     when a write landed cannot publish the rows that write just changed.
 */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

interface CacheEntry<T> {
  subject: BehaviorSubject<T | null>;
  updatedAt: number;
  inFlight?: Promise<void>;
}

@Injectable({ providedIn: 'root' })
export class BuyerServiceInvoiceSWRService {
  private cache = new Map<string, CacheEntry<any>>();

  /** Bumped by every invalidation; see the epoch check in `refresh()`. */
  private epoch = 0;

  /**
   * 30 seconds. Invoice payment state is money-adjacent: a stale "Unpaid" badge
   * after a payment is a support ticket, so this is deliberately at the short
   * end rather than the 90s used by ordinary operations lists.
   */
  private readonly TTL_MS = 30_000;

  /**
   * Stream for one key. Purely a subscription — it never triggers a fetch, so
   * there is exactly one place deciding whether a load is awaited or runs in
   * the background.
   */
  getList$(key: string): Observable<any | null> {
    return this.entryFor(key).subject.asObservable();
  }

  /**
   * Return the cached response if it is still inside the TTL, and clear a stale
   * one on the way out.
   *
   * The clearing is the point. A BehaviorSubject replays its last value to
   * whoever subscribes next, so leaving a stale response in place would paint
   * minutes-old payment state with no way for the caller to tell it from
   * fresh data. Dropping it means the caller shows its spinner and waits.
   */
  getFreshOrClear(key: string): any | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    if (Date.now() - entry.updatedAt < this.TTL_MS) {
      return entry.subject.getValue();
    }
    if (entry.subject.getValue() !== null) entry.subject.next(null);
    return null;
  }

  /**
   * Fetch through the caller's thunk and publish. Concurrent callers for the
   * same key share one request, so a chip clicked twice does not produce two
   * round trips.
   */
  async refresh(key: string, fetcher: () => Promise<any>): Promise<void> {
    const entry = this.entryFor(key);
    if (entry.inFlight) return entry.inFlight;

    // Stamped now, checked on arrival. A read already in the air when a write
    // landed is carrying pre-write rows; publishing it would put the user back
    // in front of the state they just changed.
    const startedUnder = this.epoch;

    const pending: Promise<void> = fetcher()
      .then((resp: any) => {
        if (startedUnder !== this.epoch) return; // invalidated mid-flight
        entry.updatedAt = Date.now();
        entry.subject.next(resp);
      })
      .finally(() => {
        // Only release the slot if it is still ours — an invalidation clears
        // `inFlight` and a newer request may already have claimed it.
        if (entry.inFlight === pending) entry.inFlight = undefined;
      });

    entry.inFlight = pending;

    // Deliberately NOT swallowed, so the component can raise its error state
    // rather than leave a stale list on screen presented as fresh. Background
    // revalidations must attach their own .catch().
    return pending;
  }

  /**
   * Invalidate every cached entry — list pages, archived pages and the status
   * summary together.
   *
   * That breadth is deliberate. Marking an invoice paid changes the rows AND
   * the Paid/Unpaid/Overdue amounts the chips show, and archiving changes what
   * every other page contains, so purging one key would leave the rest wrong.
   *
   * Three things happen together and all three matter:
   *  - the epoch moves, barring any in-flight read from publishing pre-write data;
   *  - `inFlight` is released, so the next `refresh()` issues a genuinely new
   *    request instead of joining that doomed one;
   *  - entries are emptied rather than deleted, because deleting would orphan
   *    the BehaviorSubject a mounted component is already subscribed to.
   */
  invalidateAll(): void {
    this.epoch++;
    this.cache.forEach((entry) => {
      entry.updatedAt = 0;
      entry.inFlight = undefined;
      entry.subject.next(null);
    });
  }

  private entryFor(key: string): CacheEntry<any> {
    let entry = this.cache.get(key);
    if (!entry) {
      entry = { subject: new BehaviorSubject<any | null>(null), updatedAt: 0 };
      this.cache.set(key, entry);
    }
    return entry;
  }
}
